// ============================================================================
// safeError.js — client-side safe user-facing error mapper (S4)
//
// Firestore / callable (HttpsError) / Auth errors must NEVER be shown raw to the
// user: their `.message` can leak internal Firestore paths, collection structure,
// provider text, or query detail. This maps an error to a SAFE, generic message
// by its (normalized) code. The structured detail stays in the console for devs;
// only the safe message reaches the UI.
//
//   import { safeUserError } from '../core/safeError.js';
//   catch (err) { setError(safeUserError(err)); }
// ============================================================================

// Generic, user-safe messages keyed by normalized error code (Firebase callable
// codes are prefixed `functions/`; Firestore/Auth codes are bare — both normalized).
const CODE_MESSAGES = Object.freeze({
  'invalid-argument': 'The request was invalid. Please check your input and try again.',
  'failed-precondition': 'This action can’t be completed right now. Please refresh and try again.',
  'out-of-range': 'The request was out of range. Please check your input and try again.',
  'unauthenticated': 'Your session has expired. Please sign in again.',
  'permission-denied': 'You don’t have permission to do that.',
  'not-found': 'We couldn’t find what you were looking for.',
  'already-exists': 'That item already exists.',
  'resource-exhausted': 'Too many requests. Please wait a moment and try again.',
  'aborted': 'The request could not be completed. Please try again.',
  'deadline-exceeded': 'The request timed out. Please try again.',
  'cancelled': 'The request was cancelled. Please try again.',
  'unavailable': 'The service is temporarily unavailable. Please try again shortly.',
  'internal': 'Something went wrong. Please try again.',
  'unknown': 'Something went wrong. Please try again.',
  // Common Firebase Auth codes → friendly copy.
  'auth/invalid-email': 'That email address looks invalid.',
  'auth/user-not-found': 'Incorrect email or password.',
  'auth/wrong-password': 'Incorrect email or password.',
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/email-already-in-use': 'An account with that email already exists.',
  'auth/network-request-failed': 'Network problem. Please check your connection and try again.',
});

const DEFAULT_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Normalize a Firebase/Firestore error code: strip the `functions/` callable
 * prefix; keep `auth/...` codes intact (they have their own friendly copy).
 * @param {string} code
 * @returns {string}
 */
function normalizeCode(code) {
  const c = String(code || '').trim();
  if (c.startsWith('auth/')) return c;
  return c.replace(/^functions\//, '');
}

/**
 * Map an unknown caught error to a SAFE, generic, user-facing message. Never
 * returns the raw `.message`. Falls back to a generic message for unknown codes.
 * @param {unknown} err
 * @returns {string}
 */
export function safeUserError(err) {
  const code = normalizeCode(err && typeof err === 'object' ? err.code : '');
  return CODE_MESSAGES[code] || DEFAULT_MESSAGE;
}

export { CODE_MESSAGES, DEFAULT_MESSAGE, normalizeCode };
export default safeUserError;
